import {
  DeviceApiError,
  pollPairing,
  register,
  setCredential,
  setCredentialChangeHandler,
} from "../api/deviceClient";
import type { HardwareMeta, RegisterResponse } from "../api/types";
import { getBridge } from "../bridge";
import { config } from "../config";
import { getFingerprint } from "../device/fingerprint";
import {
  clearIdentity,
  loadCredential,
  loadDeviceId,
  saveCredential,
  saveDeviceId,
} from "../device/identity";
import { usePlayerStore } from "../state/playerStore";
import { clearScheduleCache, loadScheduleCache } from "../state/scheduleCache";
import { retainPopForScreen } from "../sync/popBuffer";
import {
  setCredentialLostHandler,
  setDeprovisionHandler,
  startSyncLoops,
  stopSyncLoops,
} from "../sync/syncLoop";

// The provisioning state machine. Called once on boot. Drives the store; the
// React tree renders from the store.

let started = false;
let pairingTimer: ReturnType<typeof setInterval> | null = null;
// One recovery at a time: sync and heartbeat both report the same failure, and a
// second wipe/register while the first is under way only doubles pairing codes.
let recovering = false;

const WAITING_FOR_UNPAIR =
  "This screen lost its connection to the platform. Ask your operator to unpair " +
  "and re-pair it — it will show a new code on its own.";

function hardwareMeta(): HardwareMeta {
  return {
    screen_width: typeof screen !== "undefined" ? screen.width : undefined,
    screen_height: typeof screen !== "undefined" ? screen.height : undefined,
    os_version: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  };
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function goActive(): void {
  usePlayerStore.getState().setNotice(null);
  usePlayerStore.getState().setProvisioning("active");
  startSyncLoops();
}

/**
 * Register until the backend hands out a pairing code. It refuses (409) while it
 * still has this device as paired — the operator has to unpair it — and may be
 * unreachable; both are retried with back-off instead of a permanent error screen.
 */
async function registerWithRetry(fingerprint: string): Promise<RegisterResponse> {
  const delays = config.registerRetryDelaysMs;
  for (let attempt = 0; ; attempt++) {
    try {
      return await register({
        fingerprint,
        platform: getBridge().platform,
        app_version: config.appVersion,
        hardware: hardwareMeta(),
      });
    } catch (err) {
      const stillPaired = err instanceof DeviceApiError && err.status === 409;
      if (stillPaired) usePlayerStore.getState().setNotice(WAITING_FOR_UNPAIR);
      console.warn("[controller] register failed, retrying", err);
      await wait(delays[Math.min(attempt, delays.length - 1)]);
    }
  }
}

async function beginPairing(fingerprint: string): Promise<void> {
  const reg = await registerWithRetry(fingerprint);
  await saveDeviceId(reg.device_id);
  usePlayerStore.getState().setNotice(null);
  usePlayerStore.getState().setPairing({
    deviceId: reg.device_id,
    pairingCode: reg.pairing_code,
    pairingExpiresAt: reg.pairing_expires_at,
  });
  startPairingPoll(reg.device_id, fingerprint);
}

function startPairingPoll(deviceId: string, fingerprint: string): void {
  stopPairingPoll();
  let polling = false;
  pairingTimer = setInterval(async () => {
    if (polling) return; // one poll at a time
    polling = true;
    try {
      const res = await pollPairing({ device_id: deviceId, fingerprint });
      if (res.status === "paired" && res.credential) {
        stopPairingPoll();
        await saveCredential(res.credential);
        setCredential(res.credential);
        // Proof-of-play from before a re-pair is still owed to THIS screen.
        if (res.screen) {
          try {
            await retainPopForScreen(res.screen.screen_id);
          } catch {
            /* best effort — never hold up going active */
          }
        }
        goActive();
      } else if (res.status === "paired") {
        // Paired, but the one credential it mints went to a response this device
        // never got. Nothing can mint another until the operator re-pairs it.
        stopPairingPoll();
        void recoverFromLostCredential();
      } else if (res.status === "expired") {
        stopPairingPoll();
        await clearIdentity();
        void beginPairing(fingerprint); // re-register for a fresh code
      }
    } catch (err) {
      console.warn("[controller] pairing poll failed", err);
    } finally {
      polling = false;
    }
  }, config.pairingPollIntervalMs);
}

function stopPairingPoll(): void {
  if (pairingTimer) clearInterval(pairingTimer);
  pairingTimer = null;
}

/** Local provisioning state of the old pairing, minus its proof-of-play. */
async function clearPairing(): Promise<void> {
  stopSyncLoops();
  stopPairingPoll();
  setCredential(null);
  try {
    await clearIdentity(); // drops credential + device_id (keeps fingerprint)
  } catch {
    /* best effort */
  }
  try {
    await getBridge().clearCache(); // drop cached media from the old pairing
  } catch {
    /* best effort */
  }
  try {
    await clearScheduleCache(); // drop the old pairing's schedule snapshot
  } catch {
    /* best effort */
  }
}

/**
 * The device was permanently deprovisioned server-side (operator unpaired/retired
 * it, or its token generation was revoked). Wipe local provisioning state and
 * re-enter pairing so it shows a fresh code — no physical access needed.
 * Buffered proof-of-play is kept: it is filtered to the screen the device is paired
 * to next (see startPairingPoll).
 */
export async function deprovision(): Promise<void> {
  if (recovering) return;
  recovering = true;
  try {
    await clearPairing();
    usePlayerStore.getState().reset();
    usePlayerStore.getState().setProvisioning("registering");
    const fingerprint = await getFingerprint();
    await beginPairing(fingerprint);
  } catch (err) {
    usePlayerStore.getState().setError(String(err));
  } finally {
    recovering = false;
  }
}

/**
 * The backend still has this device paired, but its credential is gone (a refresh
 * response lost in transit past the grace window, or the pairing response lost).
 * Wiping would change nothing — register refuses a paired device — so keep playing
 * what is cached, say what is needed, and probe until the operator unpairs it; then
 * pair again like a fresh device.
 */
export async function recoverFromLostCredential(): Promise<void> {
  if (recovering) return;
  recovering = true;
  try {
    stopSyncLoops();
    stopPairingPoll();
    usePlayerStore.getState().setNotice(WAITING_FOR_UNPAIR);
    const fingerprint = await getFingerprint();
    const reg = await registerWithRetry(fingerprint); // succeeds once unpaired
    await clearPairing();
    usePlayerStore.getState().reset();
    await saveDeviceId(reg.device_id);
    usePlayerStore.getState().setNotice(null);
    usePlayerStore.getState().setPairing({
      deviceId: reg.device_id,
      pairingCode: reg.pairing_code,
      pairingExpiresAt: reg.pairing_expires_at,
    });
    startPairingPoll(reg.device_id, fingerprint);
  } catch (err) {
    usePlayerStore.getState().setError(String(err));
  } finally {
    recovering = false;
  }
}

function wireConnectivity(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("online", () => usePlayerStore.getState().setOnline(true));
  window.addEventListener("offline", () => usePlayerStore.getState().setOnline(false));
}

export async function startController(): Promise<void> {
  if (started) return;
  started = true;
  wireConnectivity();

  // Awaited by the device client before a refreshed credential is used, so a
  // reload right after a rotation never resumes with the revoked one.
  setCredentialChangeHandler((cred) => saveCredential(cred));
  setDeprovisionHandler(() => void deprovision());
  setCredentialLostHandler(() => void recoverFromLostCredential());

  try {
    const fingerprint = await getFingerprint();

    const existing = await loadCredential();
    if (existing) {
      setCredential(existing);
      // Rehydrate the last-good schedule BEFORE the sync loops start: if the
      // backend is unreachable, playback resumes from cached media instead of
      // sitting on "Waiting for content…". The boot sync is forced (etag
      // ignored), so a reachable backend replaces this immediately.
      const cached = await loadScheduleCache();
      if (cached) usePlayerStore.getState().setSchedule(cached);
      goActive();
      return;
    }

    usePlayerStore.getState().setProvisioning("registering");

    // If we registered before but never finished pairing, the device_id is
    // already stored — re-register to get a fresh, unexpired code.
    await loadDeviceId();
    await beginPairing(fingerprint); // retries until the backend answers
  } catch (err) {
    usePlayerStore.getState().setError(String(err));
  }
}

// test-only reset
export function __resetController(): void {
  started = false;
  recovering = false;
  stopPairingPoll();
}
